import CartaoInscritoMobile from './CartaoInscritoMobile';
import TabelaInscritos from './TabelaInscritos';
import { chaveGrupo, nomeCompletoDoGrupo } from '../vagas';
import type {
  FilaLinha,
  InfoLinhaVagas,
  InscritoGerenciar,
  SuplenteLinha,
} from '../types';

interface Props {
  itens: InscritoGerenciar[];
  fila: SuplenteLinha[];
  /** Quantos suplentes cada grupo tem (lista inteira, não só a página). */
  totais?: Map<string, number>;
  detalheMap: Map<number, Partial<InscritoGerenciar>>;
  saldoMap: Map<number, number>;
  temFoto: boolean;
  fotoUrlMap: Map<number, string>;
  mostrarParticipacao: boolean;
  equipeMap?: Map<number, string>;
  filaMap?: Map<number, FilaLinha>;
  vagasMap?: Map<number, InfoLinhaVagas>;
  selecionados?: Set<number>;
  aoAlternarSelecao?: (inscricaoId: number) => void;
  aoAbrir: (inscrito: InscritoGerenciar) => void;
}

interface Secao {
  chave: string;
  titulo: string;
  itens: InscritoGerenciar[];
}

// A aba Suplentes agrupada por grupo, respeitando a ordem da fila.
export default function ListaSuplentes({
  itens,
  fila,
  totais,
  detalheMap,
  saldoMap,
  temFoto,
  fotoUrlMap,
  mostrarParticipacao,
  equipeMap,
  filaMap,
  vagasMap,
  selecionados,
  aoAlternarSelecao,
  aoAbrir,
}: Props) {
  const secoes: Secao[] = [];
  const indice = new Map<string, number>();

  // A ordem dos grupos vem da própria fila (posição 1, 2, 3...).
  [...fila]
    .sort((a, b) => a.posicao - b.posicao)
    .forEach((f) => {
      const chave = chaveGrupo(f.modalidade_id, f.opcao_id);
      if (indice.has(chave)) return;
      indice.set(chave, secoes.length);
      secoes.push({
        chave,
        titulo: nomeCompletoDoGrupo({ grupo: f.grupo, modalidade: f.modalidade }),
        itens: [],
      });
    });

  itens.forEach((i) => {
    const det = detalheMap.get(i.inscricao_id);
    const chave = chaveGrupo(i.modalidade_id, det?.opcao_id ?? null);
    let pos = indice.get(chave);
    if (pos === undefined) {
      pos = secoes.length;
      indice.set(chave, pos);
      secoes.push({
        chave,
        titulo: nomeCompletoDoGrupo({ grupo: i.grupo, modalidade: i.modalidade }),
        itens: [],
      });
    }
    secoes[pos].itens.push(i);
  });

  const visiveis = secoes.filter((s) => s.itens.length > 0);

  // O selo do grupo conta só os suplentes daquele grupo.
  const quantosNoGrupo = (secao: Secao) => totais?.get(secao.chave) ?? secao.itens.length;

  return (
    <div className="space-y-5">
      {visiveis.map((secao) => (
        <div key={secao.chave}>
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                <i className="ri-group-line" aria-hidden />
              </span>
              <h4 className="min-w-0 truncate text-[14px] font-bold text-foreground-950">
                {secao.titulo}
              </h4>
            </div>
            <span className="shrink-0 whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-semibold text-secondary-800">
              {quantosNoGrupo(secao)} {quantosNoGrupo(secao) === 1 ? 'suplente' : 'suplentes'}
            </span>
          </div>

          <div className="hidden md:block">
            <TabelaInscritos
              inscritos={secao.itens}
              saldoMap={saldoMap}
              temFoto={temFoto}
              fotoUrlMap={fotoUrlMap}
              mostrarParticipacao={mostrarParticipacao}
              equipeMap={equipeMap}
              filaMap={filaMap}
              vagasMap={vagasMap}
              selecionados={selecionados}
              aoAlternarSelecao={aoAlternarSelecao}
              aoAbrir={aoAbrir}
            />
          </div>
          <div className="space-y-3 md:hidden">
            {secao.itens.map((i) => (
              <CartaoInscritoMobile
                key={i.inscricao_id}
                inscrito={i}
                saldo={saldoMap.get(i.inscricao_id) ?? null}
                temFoto={temFoto}
                fotoUrlMap={fotoUrlMap}
                mostrarParticipacao={mostrarParticipacao}
                equipeMap={equipeMap}
                filaMap={filaMap}
                vagasMap={vagasMap}
                selecionados={selecionados}
                aoAlternarSelecao={aoAlternarSelecao}
                aoAbrir={aoAbrir}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}